import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, CircularProgress, Alert, Card, CardContent, Chip, Button, Grid, Avatar, Stack, IconButton, Menu, MenuItem } from '@mui/material';
import { useGetAwardByIdQuery } from './api/awardApi';
import { Tabs } from '../../libs/ui/tabs/tabs';
import AddEditNominee from './components/AddEditNominee';
import { Nominee } from './types/nominee';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useDeleteNomineeMutation, useUpdateNomineeMutation, useChangeNomineeStatusMutation } from './api/nomineeApi';
import { ConfirmModal } from '../../libs/ui/modal/confirm_modal';

const parseArray = (str: string) => {
  try {
    const arr = JSON.parse(str);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const AwardDetailPage = () => {
  const { id } = useParams();
  const { data, error, isLoading } = useGetAwardByIdQuery(id!);
  const [tabIndex, setTabIndex] = useState(0);
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [editNominee, setEditNominee] = useState<Nominee | null>(null);
  const { refetch } = useGetAwardByIdQuery(id!);
  // Menu state for nominee card
  const [menuAnchorEls, setMenuAnchorEls] = useState<{ [key: string]: HTMLElement | null }>({});
  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; nominee: any | null }>({ open: false, nominee: null });
  // Status menu state
  const [statusMenu, setStatusMenu] = useState<{ anchorEl: HTMLElement | null; nominee: any | null }>({ anchorEl: null, nominee: null });
  // API hooks
  const [deleteNominee, { isLoading: isDeleting }] = useDeleteNomineeMutation();
  const [updateNominee, { isLoading: isUpdating }] = useUpdateNomineeMutation();
  const [changeNomineeStatus, { isLoading: isStatusChanging }] = useChangeNomineeStatusMutation();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, nomineeId: string) => {
    setMenuAnchorEls((prev) => ({ ...prev, [nomineeId]: event.currentTarget }));
  };
  const handleMenuClose = (nomineeId: string) => {
    setMenuAnchorEls((prev) => ({ ...prev, [nomineeId]: null }));
  };
  const handleMenuAction = (action: string, nominee: any) => {
    if (action === 'delete') {
      setDeleteDialog({ open: true, nominee });
      handleMenuClose(nominee.id);
    } else if (action === 'edit') {
      setEditNominee(nominee);
      setAddEditOpen(true);
      handleMenuClose(nominee.id);
    } else if (action === 'change-status') {
      setStatusMenu({ anchorEl: menuAnchorEls[nominee.id], nominee });
      handleMenuClose(nominee.id);
    }
  };
  // Delete confirm
  const handleDeleteConfirm = async () => {
    if (deleteDialog.nominee) {
      await deleteNominee(deleteDialog.nominee.id);
      setDeleteDialog({ open: false, nominee: null });
      refetch();
    }
  };
  // Status change
  const statusOptions = ['suggested', 'shortlisted', 'winner', 'rejected'];
  const handleStatusChange = async (status: string) => {
    if (statusMenu.nominee) {
      await changeNomineeStatus({ id: statusMenu.nominee.id, body: { status } });
      setStatusMenu({ anchorEl: null, nominee: null });
      refetch();
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">Error loading award details</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box mt={4}>
        <Alert severity="warning">Award not found</Alert>
      </Box>
    );
  }

  const award = data.data;
  const criteria = parseArray(award.criteria);
  const prizes = parseArray(award.prizes);

  // Winners and Nominees
  const winners = award.winners || [];
  const nominees = award.nominees || [];

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      {/* Basic Award Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h3" component="h1" gutterBottom>
            {award.title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Year: {award.year} | Status: <Chip label={award.status} color={award.is_active ? 'success' : 'default'} size="small" />
          </Typography>
          <Typography variant="body1" paragraph>
            {award.description}
          </Typography>
          <Typography variant="body2" paragraph>
            Ceremony: {award.ceremony_date ? new Date(award.ceremony_date).toLocaleDateString() : '-'} at {award.ceremony_location}
          </Typography>
          <Typography variant="body2" paragraph>
            Nomination Deadline: {award.nomination_deadline ? new Date(award.nomination_deadline).toLocaleDateString() : '-'}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="subtitle2">Criteria:</Typography>
              <ul style={{ margin: 0 }}>
                {criteria.map((c: string, i: number) => <li key={i}>{c}</li>)}
              </ul>
            </Box>
            <Box>
              <Typography variant="subtitle2">Prizes:</Typography>
              <ul style={{ margin: 0 }}>
                {prizes.map((p: string, i: number) => <li key={i}>{p}</li>)}
              </ul>
            </Box>
          </Stack>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Areas:</Typography>
            <Stack direction="row" spacing={1}>
              {award.areas?.map((area: any) => (
                <Chip key={area.id} label={area.name} />
              ))}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs for Winners and Nominees */}
      <Tabs
        headers={[{ label: 'Winners' }, { label: 'Nominees' }]}
        index={tabIndex}
        seperator={null}
      >
        {/* Winners Tab */}
        <Box>
          {winners.length === 0 ? (
            <Alert severity="info">No winners yet.</Alert>
          ) : (
            <Grid container spacing={2}>
              {winners.map((winner: any) => (
                <Grid item xs={12} md={6} key={winner.id}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar src={winner.photo || undefined} alt={winner.name} />
                        <Box>
                          <Typography variant="h6">{winner.name}</Typography>
                          <Typography variant="body2" color="text.secondary">{winner.status}</Typography>
                        </Box>
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 1 }}>{winner.bio_contribution}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Nominated by: {winner.nominator_name} ({winner.nominator_email}, {winner.nominator_phone})
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
        {/* Nominees Tab */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" color="primary" onClick={() => { setEditNominee(null); setAddEditOpen(true); }}>
              Add New Nominee
            </Button>
          </Box>
          {nominees.length === 0 ? (
            <Alert severity="info">No nominees yet.</Alert>
          ) : (
            <Grid container spacing={2}>
              {nominees.map((nominee: any) => (
                <Grid item xs={12} md={6} key={nominee.id}>
                  <Card 
                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: 3 } }}
                    onClick={() => {
                      setEditNominee(nominee);
                      setAddEditOpen(true);
                    }}
                  >
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar src={nominee.photo || undefined} alt={nominee.name} />
                          <Box>
                            <Typography variant="h6">{nominee.name}</Typography>
                            <Typography variant="body2" color="text.secondary">{nominee.status}</Typography>
                          </Box>
                        </Stack>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, nominee.id);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        <Menu
                          anchorEl={menuAnchorEls[nominee.id]}
                          open={Boolean(menuAnchorEls[nominee.id])}
                          onClose={() => handleMenuClose(nominee.id)}
                        >
                          <MenuItem onClick={() => handleMenuAction('change-status', nominee)}>Change Status</MenuItem>
                          <MenuItem onClick={() => handleMenuAction('edit', nominee)}>Edit</MenuItem>
                          <MenuItem onClick={() => handleMenuAction('delete', nominee)}>Delete</MenuItem>
                        </Menu>
                        {/* Status Change Menu */}
                        <Menu
                          anchorEl={statusMenu.anchorEl}
                          open={statusMenu.nominee?.id === nominee.id && Boolean(statusMenu.anchorEl)}
                          onClose={() => setStatusMenu({ anchorEl: null, nominee: null })}
                        >
                          {statusOptions.map((status) => (
                            <MenuItem
                              key={status}
                              selected={statusMenu.nominee?.status === status}
                              onClick={() => handleStatusChange(status)}
                            >
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </MenuItem>
                          ))}
                        </Menu>
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 1 }}>{nominee.bio_contribution}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Nominated by: {nominee.nominator_name} ({nominee.nominator_email}, {nominee.nominator_phone})
                      </Typography>
                      {typeof nominee.vote_count !== 'undefined' && (
                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                          Votes: {nominee.vote_count}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          <AddEditNominee
            open={addEditOpen}
            setModalOpen={setAddEditOpen}
            onSuccess={() => { setAddEditOpen(false); refetch(); setEditNominee(null); }}
            nominee={editNominee}
            awardId={award.id}
          />
        </Box>
      </Tabs>
      {/* Confirm Delete Modal */}
      <ConfirmModal
        modalOpen={deleteDialog.open}
        setModalOpen={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        title="Delete Nominee"
        message={`Are you sure you want to delete nominee "${deleteDialog.nominee?.name}"?`}
        type="primary"
        onOk={handleDeleteConfirm}
        loading={isDeleting}
      />
    </Box>
  );
};

export default AwardDetailPage; 